import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Space,
    Button,
    Row,
    Col,
    Avatar,
    Badge,
    Table,
    Modal,
    Spin,
    Tag,
    Descriptions,
    Empty,
    Calendar,
    Statistic
} from 'antd';
import {
    FileTextOutlined,
    ShoppingOutlined,
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    MailOutlined,
    PhoneOutlined,
    BankOutlined,
    TeamOutlined,
    IdcardOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import axios from 'axios';
import FormikForm from '../form/FormikForm';
import GirisCikisTakvimi from './GirisCikisTakvimi';
import './Profil.css';

const { Title, Text } = Typography;

const decodeHtmlEntities = (text) => {
    if (typeof text !== 'string' || !text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
};

const ProfilPage = () => {
    const { user, getAuthHeaders } = useAuth();
    const { darkMode } = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
    const [assignedItemsModalOpen, setAssignedItemsModalOpen] = useState(false);
    const [jobDescriptionModalOpen, setJobDescriptionModalOpen] = useState(false);
    const [leavesModalOpen, setLeavesModalOpen] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [assignedItems, setAssignedItems] = useState([]);
    const [jobDescription, setJobDescription] = useState(null);
    const [leaves, setLeaves] = useState([]);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState('');
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [formExtraData, setFormExtraData] = useState({});

    const PP_BASE_URL = 'https://dosya.atmturkiye.com';
    const BUCKET_NAME = 'atmpoint';
    const FOLDER_PATH = 'personel';

    const generateLogoUrl = (user_id) => {
        if (!user_id) return null;
        return `${PP_BASE_URL}/${BUCKET_NAME}/${FOLDER_PATH}%2F${user_id}.jpg`;
    };

    useEffect(() => {
        if (user) {
            fetchProfileData();
        }
    }, [user]);

    const formatDate = (dateString) => {
        if (!dateString || dateString === '0000-00-00' || dateString === null) return null;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return null;
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    const formatPhone = (phone) => {
        if (!phone) return '';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `0 ${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6, 8)} ${cleaned.substring(8, 10)}`;
        }
        return phone;
    };

    const calculateAge = (birthDate) => {
        if (!birthDate || birthDate === '0000-00-00' || birthDate === null) return null;
        const today = new Date();
        const birth = new Date(birthDate);
        if (isNaN(birth.getTime())) return null;
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const calculateWorkDuration = (startDate) => {
        if (!startDate || startDate === '0000-00-00' || startDate === null) return null;
        const today = new Date();
        const start = new Date(startDate);
        if (isNaN(start.getTime())) return null;

        let years = today.getFullYear() - start.getFullYear();
        let months = today.getMonth() - start.getMonth();
        let days = today.getDate() - start.getDate();

        if (days < 0) {
            months--;
            const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            days += lastMonth.getDate();
        }

        if (months < 0) {
            years--;
            months += 12;
        }

        const parts = [];
        if (years > 0) parts.push(`${years} yıl`);
        if (months > 0) parts.push(`${months} ay`);
        if (days > 0) parts.push(`${days} gün`);

        return parts.length > 0 ? parts.join(' ') : '0 gün';
    };

    const fetchProfileData = async () => {
        try {
            setLoading(true);
            const userId = user?.kullanici_no;
            const userResponse = await axios.post(
                `${API_BASE_URL}/list/execute-query/profil-kullanici`,
                { params: { id: userId } },
                { headers: getAuthHeaders() }
            );
            const userData = Array.isArray(userResponse.data) && userResponse.data.length > 0
                ? userResponse.data[0]
                : null;
            if (userData) {
                const birthDate = formatDate(userData.dogun_tarihi);
                const startDate = formatDate(userData.ise_giris_tarihi);
                const endDate = formatDate(userData.is_cikis_tarihi);
                const age = calculateAge(userData.dogun_tarihi);
                const workDuration = calculateWorkDuration(userData.ise_giris_tarihi);

                setProfileData({
                    name: userData.ad_soyad || user?.ad_soyad || 'Kullanıcı',
                    birthDate: birthDate || 'Tanımsız',
                    age: age,
                    phone: formatPhone(userData.kul_tel) + (userData.dahili ? ` | Dahili: ${userData.dahili}` : ''),
                    email: userData.kul_mail || (user?.username ? `${user.username}@atmturkiye.com` : 'email@atmturkiye.com'),
                    startDate: startDate || 'Tanımsız',
                    workDuration: workDuration || '',
                    endDate: endDate,
                    company: userData.firma || 'Tanımsız',
                    department: userData.grup || 'Tanımsız',
                    pozisyon: userData.pozisyon,
                    pozisyon_id: userData.pozisyon_id
                });            }

            const docsResponse = await axios.post(
                `${API_BASE_URL}/list/execute-query/profil-belge`,
                { params: { id: userId } },
                { headers: getAuthHeaders() }
            );
            const docsData = Array.isArray(docsResponse.data) ? docsResponse.data : [];

            const nameMap = {
                1: 'Fotoğraf',
                2: 'CV',
                3: 'Diploma',
                4: 'Sağlık Raporu',
                5: 'Nüfus Cüzdanı',
                6: 'Nüfus Kayıt Örneği',
                7: 'Kan Grubu',
                8: 'Ehliyet',
                9: 'İkametgah',
                10: 'Askerlik',
                11: 'Adli Sicil Kaydı',
                12: 'İş Başvuru Formu',
                13: 'İş Sözleşmesi',
                14: 'SGK İşe Giriş Bildirgesi',
                15: 'SGK İşten Ayrılış Bildirgesi',
                16: 'İmza Beyannamesi',
                17: 'KVKK Açık Rıza',
                99: 'Diğer'
            };

            let na = [1, 2, 3, 4, 5, 6, 8, 9, 10, 11, 12, 13, 14, 15, 16];
            if (userData?.cinsiyet == 1) {
                if (userData.is_cikis_tarihi === '0000-00-00' || !userData.is_cikis_tarihi) {
                    na = [1, 2, 3, 4, 5, 6, 8, 9, 11, 12, 13, 14, 99];
                } else {
                    na = [1, 2, 3, 4, 5, 6, 8, 9, 11, 12, 13, 14, 15, 16];
                }
            } else {
                if (userData?.is_cikis_tarihi === '0000-00-00' || !userData?.is_cikis_tarihi) {
                    na = [1, 2, 3, 4, 5, 6, 8, 9, 10, 11, 12, 13, 14, 99];
                } else {
                    na = [1, 2, 3, 4, 5, 6, 8, 9, 10, 11, 12, 13, 14, 15, 16];
                }
            }

            const docsByType = {};
            docsData.forEach(doc => {
                const tip = doc['Evrak Tipi'];
                if (!docsByType[tip]) {
                    docsByType[tip] = [];
                }
                docsByType[tip].push(doc);
            });

            const docList = na.map(tip => ({
                name: nameMap[tip],
                status: !!docsByType[tip],
                files: docsByType[tip] || []
            }));

            console.log("docList",docList);
            setDocuments(docList);

            const zimmetResponse = await axios.post(
                `${API_BASE_URL}/list/execute-query/profil-zimmet`,
                { params: { id: userId } },
                { headers: getAuthHeaders() }
            );
            const zimmetData = Array.isArray(zimmetResponse.data) ? zimmetResponse.data : [];
            const items = zimmetData.map(item => ({
                key: item.key_zimmet || item.No,
                type: item.Tip || '',
                brand: item.Marka || '',
                detail: item['Seri Numara/IMEI/Hat/Plaka'] || '',
                date: item['Zimmet Tarihi'] || ''
            }));
            setAssignedItems(items);

            try {
                if (userData?.pozisyon_id) {
                    const jobResponse = await axios.post(
                        `${API_BASE_URL}/list/execute-query/profil-gorev`,
                        { params: { id: userData.pozisyon_id } },
                        { headers: getAuthHeaders() }
                    );
                    const jobData = Array.isArray(jobResponse.data) && jobResponse.data.length > 0
                        ? jobResponse.data[0]
                        : null;

                    if (jobData) {
                        setJobDescription({
                            code: jobData.kod || '',
                            description: jobData.aciklama || '',
                            name: jobData.ad || '',
                            responsibilities: jobData.gorev_tanimi || ''
                        });
                    }
                }
            } catch (error) {
                console.error('Görev tanımı yüklenirken hata:', error);
            }

            try {
                const leavesResponse = await axios.post(
                    `${API_BASE_URL}/list/execute-query/profil-izin`,
                    { params: { id: userId } },
                    { headers: getAuthHeaders() }
                );
                const leavesData = Array.isArray(leavesResponse.data) ? leavesResponse.data : [];

                const leavesList = leavesData.map((leave, index) => ({
                    key: leave.key_izinim || index,
                    name: leave['İsim Soyisim'] || '',
                    startDate: leave.Başlangıç || '',
                    endDate: leave.Bitiş || '',
                    hours: leave.Saatler || '',
                    reason: leave['İzin Sebebi'] || '',
                    type: leave['İzin Tipi'] || '',
                    startTime: leave['Başlangıç Saati'] || '',
                    endTime: leave['Bitiş Saati'] || '',
                    approval: leave.Onay || '',
                    requestDate: leave['Talep Tarihi'] || ''
                }));
                setLeaves(leavesList);
            } catch (error) {
                console.error('İzin verileri yüklenirken hata:', error);
                setLeaves([]);
            }

        } catch (error) {
            console.error('Profil verileri yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null, extraData = {}, overrideFunction = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);            
        setCurrentUniqueValue(uniqueValue);
        setFormExtraData({
            ...extraData,
            ...(overrideFunction && { _override_function: overrideFunction })
        });
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        setCurrentUniqueValue(null);
        fetchProfileData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const handleViewFile = (file) => {
        if (!file) return;
        if (file.File && file.Dir) {
            const fileUrl = `${PP_BASE_URL}/${BUCKET_NAME}/${file.File}`;
            window.open(fileUrl, '_blank');
        } else {
            console.log('Dosya bilgisi bulunamadı:', file);
        }
    };

    const leavesColumns = [
        {
            title: 'İsim Soyisim',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Başlangıç',
            dataIndex: 'startDate',
            key: 'startDate',
        },
        {
            title: 'Bitiş',
            dataIndex: 'endDate',
            key: 'endDate',
        },
        {
            title: 'Saatler',
            dataIndex: 'hours',
            key: 'hours',
        },
        {
            title: 'İzin Sebebi',
            dataIndex: 'reason',
            key: 'reason',
        },
        {
            title: 'Onay',
            dataIndex: 'approval',
            key: 'approval',
            render: (text) => (
                <Tag color={text === 'Onaylandı' ? 'green' : 'red'}>
                    {text}
                </Tag>
            )
        },
        {
            title: 'Talep Tarihi',
            dataIndex: 'requestDate',
            key: 'requestDate',
        },
        {
            title: 'Dilekçe',
            dataIndex: 'petition',
            key: 'petition',
            render: (text, record) => (
                <Button
                    type="link"
                    size="small"
                    onClick={() => navigate(`/ik/dilekce/${record.key}`)}
                >
                    {text || 'Görüntüle'}
                </Button>
            )
        }
    ];

    const assignedItemsColumns = [
        {
            title: 'Tip',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Marka',
            dataIndex: 'brand',
            key: 'brand',
        },
        {
            title: 'Seri Numara/IMEI/Hat/Plaka',
            dataIndex: 'detail',
            key: 'detail',
        },
        {
            title: 'Zimmet Tarihi',
            dataIndex: 'date',
            key: 'date',
        }
    ];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className={`profil-container ${darkMode ? 'dark' : ''}`}>
            <Row gutter={[24, 24]}>
                {/* Sol Kolon */}
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {/* Profil Bilgileri */}
                        <Card className="profil-card">
                            <div className="profil-header">
                                <Avatar
                                    size={170}
                                    src={user?.kullanici_no ? generateLogoUrl(user.kullanici_no) : null}
                                    icon={<UserOutlined />}
                                    className="profil-avatar"
                                />
                                <Title level={2} className="profil-name">
                                    {profileData?.name || user?.ad_soyad || 'Kullanıcı'}
                                </Title>
                            </div>
                        </Card>

                        {/* İş İçin Gerekli Belgeler */}
                        <Card
                            className="profil-card clickable-card"
                            onClick={() => setDocumentsModalOpen(true)}
                        >
                            <div className="card-header">
                                <FileTextOutlined className="card-icon" />
                                <Title level={5} className="card-title">İş İçin Gerekli Belgeler</Title>
                            </div>
                            <div className="card-preview">
                                <Text type="secondary">
                                    {documents.filter(d => d.status).length} / {documents.length} belge tamamlandı
                                </Text>
                            </div>
                        </Card>

                        {/* Zimmet Edilen Eşyalar */}
                        <Card
                            className="profil-card clickable-card"
                            onClick={() => setAssignedItemsModalOpen(true)}
                        >
                            <div className="card-header">
                                <ShoppingOutlined className="card-icon" />
                                <Title level={5} className="card-title">Zimmet Edilen Eşyalar</Title>
                            </div>
                            <div className="card-preview">
                                <Text type="secondary">
                                    {assignedItems.length} eşya zimmetli
                                </Text>
                            </div>
                        </Card>

                        {/* Görev Tanımı */}
                        <Card
                            className="profil-card clickable-card"
                            onClick={() => setJobDescriptionModalOpen(true)}
                        >
                            <div className="card-header">
                                <FileTextOutlined className="card-icon" />
                                <Title level={5} className="card-title">Görev Tanımı</Title>
                            </div>
                            <div className="card-preview">
                                <Text type="secondary">
                                    {jobDescription?.code || 'Kod yok'} - {jobDescription?.description || 'Açıklama yok'}
                                </Text>
                            </div>
                        </Card>

                        {/* İzinler */}
                        <Card
                            className="profil-card clickable-card"
                            onClick={() => setLeavesModalOpen(true)}
                        >
                            <div className="card-header">
                                <CalendarOutlined className="card-icon" />
                                <Title level={5} className="card-title">İzinler</Title>
                            </div>
                            <div className="card-preview">
                                <Text type="secondary">
                                    {leaves.length} izin kaydı
                                </Text>
                            </div>
                        </Card>
                    </Space>
                </Col>

                {/* Sağ Kolon */}
                <Col xs={24} lg={16}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {/* Kişisel Bilgiler */}
                        <Card className="profil-card">
                            <Title level={5} style={{ marginBottom: 16 }}>Kişisel Bilgiler</Title>
                            <Descriptions column={2} bordered size="small">
                                <Descriptions.Item label={<><CalendarOutlined /> Doğum Tarihi</>}>
                                    {profileData?.birthDate} {profileData?.age && `(${profileData.age} yaşında)`}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><PhoneOutlined /> Telefon</>}>
                                    {profileData?.phone}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><MailOutlined /> E-Posta</>}>
                                    {profileData?.email}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><ClockCircleOutlined /> İşe Giriş Tarihi</>}>
                                    {profileData?.startDate} {profileData?.workDuration && `(${profileData.workDuration})`}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><IdcardOutlined /> İş Çıkış Tarihi</>}>
                                    {profileData?.endDate || 'Halen Çalışıyor'}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><BankOutlined /> Firma</>}>
                                    {profileData?.company}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><TeamOutlined /> Departman</>}>
                                    {profileData?.department}
                                </Descriptions.Item>
                                <Descriptions.Item label={<><UserOutlined /> Pozisyon</>}>
                                    {profileData?.pozisyon}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>

                        {/* Giriş-Çıkış Takvimi: okutmalar + resmi tatiller + izinler (ortak bileşen) */}
                        <GirisCikisTakvimi kullaniciNo={user?.kullanici_no} izinler={leaves} />
                    </Space>
                </Col>
            </Row>

            {/* Belgeler Modal */}
            <Modal
                title="İş İçin Gerekli Belgeler"
                open={documentsModalOpen}
                onCancel={() => setDocumentsModalOpen(false)}
                footer={null}
                width={600}
                className={darkMode ? 'dark-modal' : ''}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {documents.length > 0 ? (
                        documents.map((doc, index) => (
                            <div key={index} className="document-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <Space>
                                    {doc.status ? (
                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                                    ) : (
                                        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                                    )}
                                    <Text style={{ color: darkMode ? '#fff' : undefined }}>{doc.name}</Text>
                                    {doc.files && doc.files.length > 0 && (
                                        <Text type="secondary" style={{ marginLeft: 8, color: darkMode ? 'rgba(255, 255, 255, 0.65)' : undefined }}>
                                            ({doc.files.length} dosya)
                                        </Text>
                                    )}
                                </Space>
                                {doc.files && doc.files.length > 0 && (
                                    <Space>
                                        <Button type="default" size="small" onClick={() => {handleOpenForm(193, 'Özlük Belgeleri', 'ozluk', 'id', doc.files[0].ID, {user_id: user?.kullanici_no}, "Update")}}>Düzenle</Button>
                                        <Button type="link" size="small" onClick={() => handleViewFile(doc.files[0])}>Görüntüle</Button>
                                    </Space>
                                )}
                            </div>
                        ))
                    ) : (
                        <Empty description="Belge bulunamadı" />
                    )}
                </Space>
            </Modal>

            {/* Zimmet Edilen Eşyalar Modal */}
            <Modal
                title="Zimmet Edilen Eşyalar"
                open={assignedItemsModalOpen}
                onCancel={() => setAssignedItemsModalOpen(false)}
                footer={null}
                width={800}
                className={darkMode ? 'dark-modal' : ''}
            >
                {assignedItems.length > 0 ? (
                    <Table
                        columns={assignedItemsColumns}
                        scroll={{ x: 'max-content' }}
                        dataSource={assignedItems}
                        pagination={false}
                        size="small"
                        rowKey="key"
                        className={darkMode ? 'dark-table' : ''}
                    />
                ) : (
                    <Empty description="Zimmet edilen eşya bulunamadı" />
                )}
            </Modal>

            {/* Görev Tanımı Modal */}
            <Modal
                title="Görev Tanımı"
                open={jobDescriptionModalOpen}
                onCancel={() => setJobDescriptionModalOpen(false)}
                footer={null}
                width={800}
                className={darkMode ? 'dark-modal' : ''}
            >
                {jobDescription ? (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <div>
                            <Text strong style={{ color: darkMode ? '#fff' : undefined }}>Kod: </Text>
                            <Text style={{ color: darkMode ? '#fff' : undefined }}>{jobDescription.code}</Text>
                        </div>
                        <div>
                            <Text strong style={{ color: darkMode ? '#fff' : undefined }}>Açıklama: </Text>
                            <Text style={{ color: darkMode ? '#fff' : undefined }}>{jobDescription.description}</Text>
                        </div>
                        {jobDescription.responsibilities && (
                            <div>
                                <Text strong>Görev ve Sorumluluklar:</Text>
                                <div
                                    style={{
                                        marginTop: 8,
                                        padding: 16,
                                        backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
                                        borderRadius: 4,
                                        color: darkMode ? '#fff' : '#000'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(jobDescription.responsibilities) }}
                                />
                            </div>
                        )}
                    </Space>
                ) : (
                    <Empty description="Görev tanımı bulunamadı" />
                )}
            </Modal>

            {/* İzinler Modal */}
            <Modal
                title="İzinler"
                open={leavesModalOpen}
                onCancel={() => setLeavesModalOpen(false)}
                footer={null}
                width={1000}
                className={darkMode ? 'dark-modal' : ''}
            >
                {leaves.length > 0 ? (
                    <Table
                        columns={leavesColumns}
                        scroll={{ x: 'max-content' }}
                        dataSource={leaves}
                        pagination={{ pageSize: 10 }}
                        size="small"
                        rowKey="key"
                        className={darkMode ? 'dark-table' : ''}
                    />
                ) : (
                    <Empty description="İzin kaydı bulunamadı" />
                )}
            </Modal>

            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnClose={true}
                    width={800}
                    className={darkMode ? 'dark-modal' : ''}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            extraData={formExtraData}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

        </div>
    );
};

export default ProfilPage;
